import React, { useEffect, useState } from 'react';
import { ArrowRight } from 'lucide-react';
import { usePrefersReducedMotion } from '../usePrefersReducedMotion';

// Examples shown in the empty sentence, one business at a time, so visitors
// from any trade see one like theirs. Invented names; the last slot has to
// read after "me dedico a".
const EXAMPLES = [
  { businessName: 'Panadería García', city: 'Getafe', sector: 'hacer pan artesano' },
  { businessName: 'Floristería Rosa', city: 'Logroño', sector: 'vender flores y ramos de novia' },
  { businessName: 'Taller Hermanos Ruiz', city: 'Zaragoza', sector: 'reparar coches' },
  { businessName: 'Peluquería Marta', city: 'Sevilla', sector: 'cortar y teñir el pelo' },
  { businessName: 'Clínica Dental Sonrisa', city: 'Valencia', sector: 'cuidar la salud dental' },
  { businessName: 'Gestoría Arrieta', city: 'Bilbao', sector: 'llevar la contabilidad de autónomos' },
  { businessName: 'Bar La Parra', city: 'Salamanca', sector: 'servir tapas y raciones' },
  { businessName: 'Fisio Norte', city: 'Oviedo', sector: 'tratar lesiones deportivas' }
];
const EXAMPLE_MS = 3500;
const FADE_MS = 250;

// Cycles through EXAMPLES while the sentence is empty and nobody is typing
// in it; with reduced motion it just starts on a random one.
const useRotatingExample = (paused) => {
  const reduceMotion = usePrefersReducedMotion();
  const [index, setIndex] = useState(() => Math.floor(Math.random() * EXAMPLES.length));
  const [fading, setFading] = useState(false);

  useEffect(() => {
    if (paused || reduceMotion) return undefined;
    let swap;
    const timer = setInterval(() => {
      setFading(true);
      swap = setTimeout(() => {
        setIndex((i) => (i + 1) % EXAMPLES.length);
        setFading(false);
      }, FADE_MS);
    }, EXAMPLE_MS);
    return () => {
      clearInterval(timer);
      clearTimeout(swap);
      setFading(false);
    };
  }, [paused, reduceMotion]);

  return { example: EXAMPLES[index], fading };
};

// An input that sits inside the headline-sized sentence, so the form reads
// as "Mi negocio se llama ___, está en ___ …" ("se llama", not "es", so
// people type the business name, not its sector). A hidden mirror of the
// text sizes it to its content (the `size` attribute is far too wide for
// proportional fonts).
const Slot = ({ id, label, placeholder, value, onChange, autoComplete, fading }) => (
  <span className="lp-slot-wrap">
    <label htmlFor={id} className="sr-only">{label}</label>
    <span aria-hidden="true" className="lp-slot-mirror">{value || placeholder}</span>
    <input
      id={id}
      name={id}
      type="text"
      className={`lp-slot${fading ? ' lp-slot--fading' : ''}`}
      placeholder={placeholder}
      value={value}
      onChange={onChange}
      autoComplete={autoComplete}
      required
    />
  </span>
);

const CheckForm = ({ onSubmit, isLoading }) => {
  const [values, setValues] = useState({ businessName: '', city: '', sector: '', website: '' });
  const [error, setError] = useState('');
  const [sentenceFocused, setSentenceFocused] = useState(false);
  const started = Boolean(values.businessName || values.city || values.sector);
  const { example, fading } = useRotatingExample(sentenceFocused || started);

  const update = (field) => (event) => {
    const { value } = event.target;
    setValues((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const trimmed = {
      businessName: values.businessName.trim(),
      city: values.city.trim(),
      sector: values.sector.trim(),
      website: values.website.trim()
    };

    if (!trimmed.businessName || !trimmed.city || !trimmed.sector) {
      setError('Completa la frase: el nombre de tu negocio, la ciudad y a qué te dedicas.');
      return;
    }

    setError('');
    onSubmit(trimmed);
  };

  return (
    <form onSubmit={handleSubmit} noValidate>
      <p className="lp-sentence" onFocus={() => setSentenceFocused(true)} onBlur={() => setSentenceFocused(false)}>
        Mi negocio se llama{' '}
        <Slot
          id="businessName"
          label="Nombre de tu negocio"
          placeholder={example.businessName}
          value={values.businessName}
          onChange={update('businessName')}
          fading={fading}
          autoComplete="organization"
        />
        , está en{' '}
        <Slot
          id="city"
          label="Ciudad o barrio"
          placeholder={example.city}
          value={values.city}
          onChange={update('city')}
          fading={fading}
          autoComplete="address-level2"
        />{' '}
        y me dedico a{' '}
        <Slot
          id="sector"
          label="A qué se dedica tu negocio"
          placeholder={example.sector}
          value={values.sector}
          onChange={update('sector')}
          fading={fading}
          autoComplete="off"
        />
        .
      </p>

      <div className="mt-8 flex flex-col gap-4 sm:flex-row sm:items-center">
        <button type="submit" className="lp-btn" disabled={isLoading}>
          {isLoading ? 'Preguntando…' : 'Pregúntale a la IA'}
          <ArrowRight size={20} strokeWidth={2.6} aria-hidden="true" />
        </button>

        <div className="sm:max-w-[260px] sm:flex-1">
          <label htmlFor="website" className="sr-only">Tu web (opcional)</label>
          <input
            id="website"
            name="website"
            type="url"
            inputMode="url"
            className="lp-field"
            placeholder="tuweb.com (opcional)"
            value={values.website}
            onChange={update('website')}
            autoComplete="url"
          />
        </div>
      </div>

      {error && (
        <p role="alert" className="mt-4 text-sm font-medium text-[var(--lp-alarm)]">
          {error}
        </p>
      )}

      <p className="mt-6 lp-mono text-[11px] uppercase tracking-[0.14em] text-[var(--lp-muted)]">
        Gratis · Sin registro · Resultado en menos de un minuto
      </p>
    </form>
  );
};

export default CheckForm;
